import { Home, Building2, ChevronRight, ChevronUp, LogOut, Settings, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import { getConfig } from '@/config';
import { logoutUser } from '@/features/auth';
import { clearUser } from '@/features/users/usersSlice';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import ModeToggle from '@/components/mode-toggle';
import { useFilteredNavigation } from '@/hooks/useFilteredNavigation';
import { Permissions } from '@/lib/permissions';
import { getFullName, getInitials } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import {
  selectActiveMembershipForCurrentUser,
  selectActiveOrganizationId,
  selectCurrentUserProfile,
} from '@/store/selectors/authorizationSelectors';

export function AppSidebar() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUserProfile);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const activeMembership = useAppSelector(selectActiveMembershipForCurrentUser);
  const navigationItems = useFilteredNavigation();
  const { hasAnyPermission, isSuperAdmin } = usePermissions();
  const activeContextOrganizationId = activeOrganizationId ?? activeMembership?.id;
  const organizationDetailsUrl =
    activeContextOrganizationId && hasAnyPermission([Permissions.MyOrgRead, Permissions.MyOrgUpdate])
      ? `/admin/organizations/${encodeURIComponent(activeContextOrganizationId)}`
      : null;
  const primaryItems = navigationItems.filter((item) => !item.superAdminOnly);
  const adminItems = navigationItems.filter((item) => item.superAdminOnly);
  const [isAdminGroupOpen, setIsAdminGroupOpen] = useState(true);
  const runtimeConfig = getConfig();
  const environmentLabel = runtimeConfig.env?.toUpperCase();
  const pathOverrideLabel = runtimeConfig.developmentPathOverride?.trim();
  const showEnvironmentDetails = runtimeConfig.env && runtimeConfig.env !== 'prod';

  const isItemActive = (url: string) => {
    if (url === '/') return location.pathname === '/';
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };
  const hasActiveAdminItem = adminItems.some((item) => isItemActive(item.url));

  useEffect(() => {
    if (hasActiveAdminItem) setIsAdminGroupOpen(true);
  }, [hasActiveAdminItem]);

  const toggleAdminGroup = () => {
    setIsAdminGroupOpen((prev) => !prev);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    dispatch(clearUser());
    navigate('/login');
  };

  return (
    <Sidebar>
      <SidebarHeader className="border-b p-4">
        <Link to="/" className="flex items-center justify-between">
          <div className="flex items-start gap-2">
            <Home className="h-5 w-5" />
            <div className="flex flex-col">
              <span className="text-lg leading-5 font-semibold">HomeVein</span>
              {showEnvironmentDetails ? (
                <span className="text-muted-foreground mt-1 text-[11px] leading-3 font-medium tracking-wide uppercase">
                  {environmentLabel}
                  {pathOverrideLabel ? <span className="normal-case"> · override: {pathOverrideLabel}</span> : null}
                </span>
              ) : null}
            </div>
          </div>
          <ModeToggle />
        </Link>
        {isSuperAdmin && (
          <Badge className="mt-2 bg-red-600 text-white hover:bg-red-700">
            <Shield className="h-3 w-3" />
            SUPER ADMIN
          </Badge>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {primaryItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isItemActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {!isSuperAdmin && organizationDetailsUrl ? (
                <SidebarMenuItem key="manage-organization">
                  <SidebarMenuButton asChild isActive={isItemActive(organizationDetailsUrl)}>
                    <Link to={organizationDetailsUrl}>
                      <Building2 className="h-4 w-4" />
                      <span>Organization</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {adminItems.length > 0 ? (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupAction
              type="button"
              aria-label={isAdminGroupOpen ? 'Collapse Admin section' : 'Expand Admin section'}
              onClick={toggleAdminGroup}
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${isAdminGroupOpen ? 'rotate-90' : ''}`} />
            </SidebarGroupAction>
            {isAdminGroupOpen ? (
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isItemActive(item.url)}>
                        <Link to={item.url}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            ) : null}
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      <SidebarFooter className="border-t">
        <div className="flex items-center justify-between p-2">
          <DropdownMenu>
            <DropdownMenuTrigger className="hover:bg-accent flex w-full items-center gap-2 rounded-md p-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{getInitials(user)}</AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col items-start">
                <span className="truncate text-sm font-medium">{getFullName(user)}</span>
                <span className="text-muted-foreground truncate text-xs">{user?.email}</span>
              </div>
              <ChevronUp className="ml-auto h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/settings">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
